/**
 * SPP-655 Phase A — client-side transcript chunking helpers.
 * Large chunks at the SPP-654 performance ceiling; short media → one chunk.
 */

export type TranscriptBlock = {
  data?: { start?: number; end?: number };
  children?: unknown[];
  [key: string]: unknown;
};

export type TranscriptChunkMeta = {
  chunkId: string;
  ordinal: number;
  /** Inclusive global block index in the full document */
  globalStartIndex: number;
  blockCount: number;
  startTime: number;
  endTime: number;
};

export type SplitTranscriptResult<T extends TranscriptBlock = TranscriptBlock> =
  {
    index: TranscriptChunkMeta[];
    chunks: Record<string, T[]>;
  };

/**
 * Max caption/paragraph blocks per chunk.
 * SPP-675-2 DWCM: 200 blocks × up to 3-window merge ≈ 600 rows max in Slate.
 */
export const CHUNK_MAX_BLOCKS = 200;

/** SPP-675-2 — boundary trigger zone (fraction of chunk height). */
export const CHUNK_WINDOW_ALPHA = 0.2;

/** SPP-675-2 — evict far chunk once viewport penetrates this far into new chunk. */
export const CHUNK_WINDOW_CLEANUP_RATIO = 0.4;

/** SPP-675-2 — max consecutive chunks merged in one Slate document. */
export const CHUNK_WINDOW_MAX = 3;

/** Progress-bar seek loads only the target chunk first (~50 blocks); scroll catch-up expands. */
export const CHUNK_SEEK_WINDOW_MAX = 1;

/** Find next/prev loads only the target chunk (same fast path as seek). */
export const CHUNK_FIND_WINDOW_MAX = 1;

/** Stable id for chunk-store ↔ Redux transcript binding (media id + shape). */
export const computeTranscriptDocumentKey = (
  nodes: TranscriptBlock[] | null | undefined,
  mediaKey?: string | null,
): string => {
  const prefix = mediaKey ? `${mediaKey}:` : '';
  if (!nodes?.length) return `${prefix}empty`;
  const first = nodes[0]?.data?.start ?? '';
  const mid = nodes[nodes.length >> 1]?.data?.start ?? '';
  const last = nodes[nodes.length - 1]?.data?.start ?? '';
  const firstText =
    nodes[0]?.children?.[0] != null &&
    typeof (nodes[0].children[0] as { text?: string }).text === 'string'
      ? (nodes[0].children[0] as { text: string }).text.slice(0, 32)
      : '';
  return `${prefix}${nodes.length}:${first}:${mid}:${last}:${firstText}`;
};

/** Boot with one chunk for fast first paint; expands to CHUNK_WINDOW_MAX on scroll. */
export const CHUNK_DWCM_INITIAL_WINDOW_CHUNKS = 1;

/** Initial DWCM window ordinals (typically a single chunk). */
export const dwcmInitialWindow = (indexLength: number): ActiveWindow => {
  if (indexLength <= 0) return { startOrdinal: 0, endOrdinal: 0 };
  const end = Math.min(indexLength - 1, CHUNK_DWCM_INITIAL_WINDOW_CHUNKS - 1);
  return { startOrdinal: 0, endOrdinal: end };
};

/** O(1) window around a chunk ordinal — used for progress-bar seek (not scroll settle). */
export const resolveWindowAroundOrdinal = (
  ordinal: number,
  indexLength: number,
  maxChunks: number = CHUNK_WINDOW_MAX,
): ActiveWindow => {
  if (indexLength <= 0) return { startOrdinal: 0, endOrdinal: 0 };
  const ord = Math.max(0, Math.min(indexLength - 1, ordinal));
  const span = Math.min(maxChunks, indexLength);
  let start = Math.max(0, ord - Math.floor((span - 1) / 2));
  let end = Math.min(indexLength - 1, start + span - 1);
  start = Math.max(0, end - span + 1);
  return { startOrdinal: start, endOrdinal: end };
};

/** Shorter cooldown for DWCM incremental steps (legacy uses CHUNK_LOAD_SCROLL_COOLDOWN_MS). */
export const CHUNK_DWCM_SCROLL_COOLDOWN_MS = 200;

/** Throttle window catch-up while user is actively stress-scrolling. */
export const CHUNK_DWCM_CATCHUP_MS = 200;

/** Min interval between HTML media currentTime updates during progress-bar scrub. */
export const CHUNK_MEDIA_SEEK_THROTTLE_MS = 150;

/** Coalesce rapid progress-bar release/click seeks while a window load is in flight. */
export const CHUNK_PROGRESS_SEEK_RELEASE_DEBOUNCE_MS = 50;

/** Min interval between DWCM Slate window remounts during rapid seek / catch-up. */
export const CHUNK_WINDOW_LOAD_MIN_INTERVAL_MS = 250;

/** Ignore video seeking/seeked handlers after progress-bar seek (ms). */
export const CHUNK_VIDEO_SEEK_IGNORE_MS = 1200;

/** SPP-675-2 — overlapping window chunk manager (always enabled). */
export const TRANSCRIPT_DWCM_ENABLED = true;

export type ActiveWindow = {
  startOrdinal: number;
  endOrdinal: number;
};

export type ChunkBand = {
  ordinal: number;
  chunkId: string;
  offsetTop: number;
  height: number;
};

export type ScrollWindowInput = {
  scrollTop: number;
  viewportHeight: number;
  bands: ChunkBand[];
  alpha?: number;
};

/** Seconds before chunk end to warm-mount the next chunk while playing */
export const CHUNK_PLAYBACK_PREFETCH_LEAD_SEC = 1.5;

/** Approx row height for inactive chunk placeholders (matches visibility overscan unit) */
export const CHUNK_PLACEHOLDER_ROW_PX = 80;

/**
 * Placeholder rows are only an estimate; once a chunk has been rendered we use
 * its measured height instead and recalibrate the estimate for chunks not seen
 * yet. Clamped so one odd measurement cannot wreck the scroll model.
 */
export const CHUNK_ROW_PX_MIN = 24;
export const CHUNK_ROW_PX_MAX = 240;

/** Ignore a measurement below this — the editor has not painted yet. */
export const CHUNK_MIN_MEASURED_PX = 40;

/** Skip re-layout for height changes smaller than this. */
export const CHUNK_HEIGHT_EPSILON_PX = 24;

/** Clamp per-block height learned from a measured chunk/window. */
export const clampChunkRowPx = (px: number) =>
  Math.min(CHUNK_ROW_PX_MAX, Math.max(CHUNK_ROW_PX_MIN, px));

/** Estimated chunk slot height from block count and optional calibrated row px. */
export const estimateChunkHeight = (
  blockCount: number,
  rowPx: number = CHUNK_PLACEHOLDER_ROW_PX,
) => Math.max(blockCount, 1) * rowPx;

/**
 * Product invariant: ActiveWindow is a contiguous ordinal range with |W| ≤ maxChunks.
 * (Gaps like {1,3} without 2 are never representable as start..end — enforce span cap.)
 */
export const isContiguousActiveWindow = (
  window: ActiveWindow,
  maxChunks: number = CHUNK_WINDOW_MAX,
): boolean => {
  if (window.endOrdinal < window.startOrdinal) return false;
  return window.endOrdinal - window.startOrdinal + 1 <= maxChunks;
};

/**
 * Clamp to a contiguous band within index bounds and at most `maxChunks` ordinals.
 * When shrinking, keep `preferOrdinal` inside the window when possible.
 */
export const clampContiguousWindow = (
  window: ActiveWindow,
  indexLength: number,
  maxChunks: number = CHUNK_WINDOW_MAX,
  preferOrdinal?: number,
): ActiveWindow => {
  if (indexLength <= 0) return { startOrdinal: 0, endOrdinal: 0 };
  const maxOrd = indexLength - 1;
  const span = Math.min(Math.max(1, maxChunks), indexLength);
  let start = Math.max(0, Math.min(window.startOrdinal, maxOrd));
  let end = Math.max(start, Math.min(window.endOrdinal, maxOrd));
  if (end - start + 1 <= span) {
    return { startOrdinal: start, endOrdinal: end };
  }
  const pivot =
    preferOrdinal != null
      ? Math.max(start, Math.min(end, preferOrdinal))
      : start + Math.floor((end - start) / 2);
  start = Math.max(0, Math.min(pivot - Math.floor((span - 1) / 2), maxOrd));
  end = Math.min(maxOrd, start + span - 1);
  start = Math.max(0, end - span + 1);
  return { startOrdinal: start, endOrdinal: end };
};

/**
 * After a live Slate window measure, calibrate per-chunk slot heights with one row px
 * so spacer bands stay monotonic with ordinal order (avoids stale far-chunk heights).
 */
export const calibrateSlotHeightsFromWindowMeasure = (
  index: TranscriptChunkMeta[],
  window: ActiveWindow,
  measuredPx: number,
  prevSlots: Record<string, number>,
  epsilon: number = CHUNK_HEIGHT_EPSILON_PX,
): {
  nextSlots: Record<string, number>;
  rowPx: number;
  changed: boolean;
} => {
  let totalBlocks = 0;
  for (let ord = window.startOrdinal; ord <= window.endOrdinal; ord++) {
    const meta = index[ord];
    if (meta) totalBlocks += meta.blockCount;
  }
  if (totalBlocks <= 0 || measuredPx < CHUNK_MIN_MEASURED_PX) {
    return {
      nextSlots: { ...prevSlots },
      rowPx: CHUNK_PLACEHOLDER_ROW_PX,
      changed: false,
    };
  }

  const rowPx = clampChunkRowPx(measuredPx / totalBlocks);
  const nextSlots = { ...prevSlots };
  let changed = false;

  for (const meta of index) {
    const next = Math.round(estimateChunkHeight(meta.blockCount, rowPx));
    const prev = nextSlots[meta.chunkId];
    if (prev == null || Math.abs(prev - next) >= epsilon) {
      nextSlots[meta.chunkId] = next;
      changed = true;
    }
  }

  return { nextSlots, rowPx, changed };
};

/**
 * Live merged-editor band inside the scroll root (px from document top).
 * Prefer this over cumulative height estimates for spacer/boundary checks.
 */
export const measureEditorBandInScrollRoot = (
  scrollRoot: HTMLElement,
  editorWindowEl: HTMLElement,
  editorEl: HTMLElement,
): { top: number; bottom: number } | null => {
  const measured = Math.round(editorEl.offsetHeight);
  if (measured < CHUNK_MIN_MEASURED_PX) return null;
  const rootRect = scrollRoot.getBoundingClientRect();
  const winRect = editorWindowEl.getBoundingClientRect();
  const top = scrollRoot.scrollTop + (winRect.top - rootRect.top);
  return { top, bottom: top + measured };
};

/**
 * Skeleton rows shown in the next chunk slot when the active chunk's end is
 * in view — cues that more transcript can load there (avoids a blank seam).
 */
export const CHUNK_SEAM_PEEK_ROWS = 10;

/** Sticky peek rows pinned to the scroll-root viewport top/bottom during load. */
export const CHUNK_VIEWPORT_EDGE_PEEK_ROWS = 4;

/** How far below the viewport to start the next-chunk peek cue (px). */
export const CHUNK_SEAM_PEEK_LEAD_PX = 120;

/**
 * After the last scroll event, wait this long before starting a chunk load.
 * Scrollbar drags emit many events — only load once the gesture stops.
 */
export const CHUNK_SCROLL_SETTLE_MS = 320;

/** Delay after showing skeleton before mounting Slate (lets UI paint first). */
export const CHUNK_MOUNT_DELAY_MS = 80;

/**
 * After a chunk load finishes, ignore scroll-driven loads this long.
 * Mounting Slate often fires layout/scroll events that would bounce to another chunk.
 */
export const CHUNK_LOAD_SCROLL_COOLDOWN_MS = 450;

/**
 * Grace period before the fallback resume fires, so the normal mount-frame
 * resume (which also snaps to the chunk start) wins when it happens.
 */
export const CHUNK_RESUME_WATCHDOG_MS = 400;

/**
 * Find chunk under viewport center using cumulative placeholder heights.
 * No DOM layout reads — safe to call from scroll handlers.
 */
export const findChunkIdByScrollOffset = (
  index: TranscriptChunkMeta[],
  scrollTop: number,
  viewportHeight: number,
  heightFor: (meta: TranscriptChunkMeta) => number,
): string | null => {
  if (!index.length) return null;
  const target = scrollTop + viewportHeight / 2;
  let acc = 0;
  for (let i = 0; i < index.length; i++) {
    const h = heightFor(index[i]);
    if (target <= acc + h || i === index.length - 1) {
      return index[i].chunkId;
    }
    acc += h;
  }
  return index[index.length - 1]?.chunkId ?? null;
};

/** Keep active chunk while its slot still intersects the viewport (px). */
export const CHUNK_SCROLL_HYSTERESIS_PX = 48;

const chunkRangeById = (
  index: TranscriptChunkMeta[],
  chunkId: string,
  heightFor: (meta: TranscriptChunkMeta) => number,
): { top: number; bottom: number } | null => {
  let top = 0;
  for (let i = 0; i < index.length; i++) {
    const h = heightFor(index[i]);
    if (index[i].chunkId === chunkId) {
      return { top, bottom: top + h };
    }
    top += h;
  }
  return null;
};

/**
 * Scroll → chunk with boundary hysteresis.
 * Prefer staying on the active chunk while any of it is still on screen so the
 * last rows/words are not unmounted the instant the viewport center crosses
 * into the next chunk.
 */
export const resolveScrollTargetChunkId = (
  index: TranscriptChunkMeta[],
  scrollTop: number,
  viewportHeight: number,
  heightFor: (meta: TranscriptChunkMeta) => number,
  activeChunkId: string | null | undefined,
  hysteresisPx: number = CHUNK_SCROLL_HYSTERESIS_PX,
): string | null => {
  const centerId = findChunkIdByScrollOffset(
    index,
    scrollTop,
    viewportHeight,
    heightFor,
  );
  if (!activeChunkId || !centerId || activeChunkId === centerId) {
    return centerId;
  }

  const range = chunkRangeById(index, activeChunkId, heightFor);
  if (!range) return centerId;

  const viewTop = scrollTop;
  const viewBottom = scrollTop + viewportHeight;
  const margin = Math.max(0, hysteresisPx);
  const stillVisible =
    range.bottom > viewTop + margin && range.top < viewBottom - margin;

  return stillVisible ? activeChunkId : centerId;
};

export const chunkIdForOrdinal = (ordinal: number): string =>
  `c${String(ordinal).padStart(4, '0')}`;

const blockStart = (block: TranscriptBlock): number => {
  const s = block?.data?.start;
  return typeof s === 'number' && !Number.isNaN(s) ? s : 0;
};

const blockEnd = (block: TranscriptBlock): number => {
  const e = block?.data?.end;
  if (typeof e === 'number' && !Number.isNaN(e)) return e;
  const children = block?.children as
    | { data?: { dataEnd?: number } }[]
    | undefined;
  const last = children?.[children.length - 1];
  const de = last?.data?.dataEnd;
  if (typeof de === 'number' && !Number.isNaN(de)) return de;
  return blockStart(block);
};

/** Index of the first caption row with a valid `data.start`. */
export const findFirstCaptionIndex = (
  nodes: TranscriptBlock[] | null | undefined,
): number => {
  const list = Array.isArray(nodes) ? nodes : [];
  for (let i = 0; i < list.length; i++) {
    const start = list[i]?.data?.start;
    if (typeof start === 'number' && !Number.isNaN(start)) return i;
  }
  return 0;
};

/** Start time of the first timed caption row, or null when none exist. */
export const findFirstCaptionStart = (
  nodes: TranscriptBlock[] | null | undefined,
): number | null => {
  const list = Array.isArray(nodes) ? nodes : [];
  const idx = findFirstCaptionIndex(list);
  const start = list[idx]?.data?.start;
  return typeof start === 'number' && !Number.isNaN(start) ? start : null;
};

/** True when playhead is still before the first timed caption (e.g. media at 0, first line at 2s). */
export const isBeforeFirstCaptionTime = (
  nodes: TranscriptBlock[] | null | undefined,
  time: number,
  epsilon = 0.05,
): boolean => {
  if (time == null || Number.isNaN(time)) return false;
  const firstStart = findFirstCaptionStart(nodes);
  if (firstStart == null) return time <= epsilon;
  return time < firstStart;
};

/**
 * Global row index for the caption active at `time`.
 * Uses last block with start <= time (covers gaps and post-end silence).
 * When playhead is before the first timed row, returns that first row.
 */
export const findCaptionIndexForTime = (
  nodes: TranscriptBlock[] | null | undefined,
  time: number,
): number => {
  const list = Array.isArray(nodes) ? nodes : [];
  if (!list.length || time == null || Number.isNaN(time)) return 0;

  const firstIdx = findFirstCaptionIndex(list);
  const firstStart = list[firstIdx]?.data?.start;
  if (typeof firstStart === 'number' && time < firstStart) {
    return firstIdx;
  }

  let lo = 0;
  let hi = list.length - 1;
  let ans = firstIdx;

  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const start = list[mid]?.data?.start;
    if (typeof start !== 'number' || Number.isNaN(start)) {
      hi = mid - 1;
      continue;
    }
    if (start <= time) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  return ans;
};

/** Caption block active at `time` — always returns a row when the doc is non-empty. */
export const findCaptionBlockForTime = (
  nodes: TranscriptBlock[] | null | undefined,
  time: number,
): TranscriptBlock | null => {
  const list = Array.isArray(nodes) ? nodes : [];
  if (!list.length) return null;
  return list[findCaptionIndexForTime(list, time)] ?? null;
};

/** Word dataStart active at `time` within one caption block. */
export const findWordStartForTime = (
  block: TranscriptBlock | null | undefined,
  time: number,
): number | null => {
  const words = block?.children as
    | { data?: { dataStart?: number } }[]
    | undefined;
  if (!words?.length || time == null || Number.isNaN(time)) return null;

  let lo = 0;
  let hi = words.length - 1;
  let ans = 0;

  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const ws = words[mid]?.data?.dataStart;
    if (typeof ws !== 'number' || Number.isNaN(ws)) {
      hi = mid - 1;
      continue;
    }
    if (ws <= time) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  const wordStart = words[ans]?.data?.dataStart;
  return typeof wordStart === 'number' ? wordStart : null;
};

/** Metadata-only chunk index — no block copies (lazy materialization in store). */
export const buildTranscriptChunkIndex = (
  nodes: TranscriptBlock[] | null | undefined,
  maxBlocks: number = CHUNK_MAX_BLOCKS,
): TranscriptChunkMeta[] => {
  const list = Array.isArray(nodes) ? nodes : [];
  const ceiling = Math.max(1, maxBlocks);
  if (!list.length) return [];

  const index: TranscriptChunkMeta[] = [];
  for (let offset = 0, ordinal = 0; offset < list.length; ordinal++) {
    const end = Math.min(offset + ceiling, list.length);
    const chunkId = chunkIdForOrdinal(ordinal);
    index.push({
      chunkId,
      ordinal,
      globalStartIndex: offset,
      blockCount: end - offset,
      startTime: blockStart(list[offset]),
      endTime: blockEnd(list[end - 1]),
    });
    offset = end;
  }
  return index;
};

export const splitTranscriptToChunks = <T extends TranscriptBlock>(
  nodes: T[] | null | undefined,
  maxBlocks: number = CHUNK_MAX_BLOCKS,
): SplitTranscriptResult<T> => {
  const list = Array.isArray(nodes) ? nodes : [];
  if (!list.length) {
    return { index: [], chunks: {} };
  }

  const index = buildTranscriptChunkIndex(list, maxBlocks);
  const chunks: Record<string, T[]> = {};
  for (const meta of index) {
    chunks[meta.chunkId] = list.slice(
      meta.globalStartIndex,
      meta.globalStartIndex + meta.blockCount,
    );
  }
  return { index, chunks };
};

export const stitchChunks = <T extends TranscriptBlock>(
  index: TranscriptChunkMeta[],
  chunks: Record<string, T[]>,
): T[] => {
  const ordered = [...index].sort((a, b) => a.ordinal - b.ordinal);
  const out: T[] = [];
  for (const meta of ordered) {
    const part = chunks[meta.chunkId];
    if (part?.length) out.push(...part);
  }
  return out;
};

/**
 * Recompute cumulative globalStartIndex / blockCount.
 * Only dirty chunks adopt live cache length — stale/empty cache must not
 * shrink the index (find paths and window merge rely on stable boundaries).
 */
export const recomputeGlobalStartIndices = (
  index: TranscriptChunkMeta[],
  chunks: Record<string, TranscriptBlock[]>,
  dirtyIds?: Set<string>,
): void => {
  let offset = 0;
  const ordered = [...index].sort((a, b) => a.ordinal - b.ordinal);
  for (const meta of ordered) {
    const liveLen = chunks[meta.chunkId]?.length;
    const useLive =
      dirtyIds?.has(meta.chunkId) && typeof liveLen === 'number';
    const count = useLive ? liveLen : meta.blockCount;
    meta.globalStartIndex = offset;
    if (useLive) {
      meta.blockCount = liveLen;
    }
    offset += count;
  }
};

/** Global caption rows touched by a finder range (anchor through focus, inclusive). */
export const globalRowsCoveredByRange = (range: {
  anchor?: { path?: number[] };
  focus?: { path?: number[] };
}): number[] => {
  const a = range?.anchor?.path?.[0];
  if (typeof a !== 'number') return [];
  const f = range?.focus?.path?.[0];
  const lo = typeof f === 'number' ? Math.min(a, f) : a;
  const hi = typeof f === 'number' ? Math.max(a, f) : a;
  const rows: number[] = [];
  for (let r = lo; r <= hi; r++) rows.push(r);
  return rows;
};

/** Binary search: chunk containing media time (by startTime/endTime). */
export const findChunkMetaForTime = (
  index: TranscriptChunkMeta[],
  time: number,
): TranscriptChunkMeta | null => {
  if (!index.length) return null;
  if (time <= index[0].startTime) return index[0];
  let lo = 0;
  let hi = index.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const m = index[mid];
    if (time < m.startTime) hi = mid - 1;
    else if (time > m.endTime && mid < index.length - 1) lo = mid + 1;
    else return m;
  }
  return index[Math.max(0, Math.min(index.length - 1, lo))];
};

export const findChunkMetaForGlobalRow = (
  index: TranscriptChunkMeta[],
  globalRow: number,
  _chunks?: Record<string, TranscriptBlock[]>,
): TranscriptChunkMeta | null => {
  if (!index.length || globalRow < 0) return null;
  for (const meta of index) {
    const end = meta.globalStartIndex + meta.blockCount;
    if (globalRow >= meta.globalStartIndex && globalRow < end) return meta;
  }
  return index[index.length - 1] ?? null;
};

export const findChunkMetaForBlockStart = (
  index: TranscriptChunkMeta[],
  chunks: Record<string, TranscriptBlock[]>,
  blockStartTime: number,
): TranscriptChunkMeta | null => {
  for (const meta of index) {
    const nodes = chunks[meta.chunkId];
    if (!nodes?.length) continue;
    if (
      nodes.some((n) => {
        const s = n?.data?.start;
        return s != null && String(s) === String(blockStartTime);
      })
    ) {
      return meta;
    }
  }
  return findChunkMetaForTime(index, blockStartTime);
};

export const globalRowToLocal = (
  meta: TranscriptChunkMeta,
  globalRow: number,
): number => globalRow - meta.globalStartIndex;

export const localRowToGlobal = (
  meta: TranscriptChunkMeta,
  localRow: number,
): number => meta.globalStartIndex + localRow;

/** What a multi-chunk shell slot should render. */
export type ChunkSlotRole = 'loading' | 'active' | 'placeholder' | 'peek';

export type ResolveChunkSlotRoleInput = {
  chunkId: string;
  activeChunkId: string | null;
  /** Chunk whose nodes have been prepared for the mounted Slate editor. */
  boundChunkId: string | null;
  /**
   * Settled preparation target. Skeleton keys off this — not scroll direction
   * or global editor visibility (`allowEditorMount`).
   */
  loadingChunkId: string | null;
  /**
   * Neighbor chunk to paint as a seam peek skeleton when the active chunk's
   * end (or start) is visible — usually the next ordinal.
   */
  peekChunkId: string | null;
  /** True while the scrollbar is moving — keep slots cheap. */
  scrollFrozen: boolean;
  allowEditorMount: boolean;
};

/**
 * Resolve a chunk slot's UI role.
 * Rapid scroll → placeholders only. After settle, the preparing chunk shows
 * the skeleton until Slate is allowed to mount for the active chunk.
 * When the active chunk's end is in view, the next chunk may be `peek`.
 */
export const resolveChunkSlotRole = ({
  chunkId,
  activeChunkId,
  boundChunkId,
  loadingChunkId,
  peekChunkId,
  scrollFrozen,
  allowEditorMount,
}: ResolveChunkSlotRoleInput): ChunkSlotRole => {
  if (scrollFrozen) {
    if (loadingChunkId && chunkId === loadingChunkId) {
      return 'loading';
    }
    return 'peek';
  }

  // Only mount Slate after this chunk's document has been prepared. A load
  // changes activeChunkId before the preparation effect updates editorDocument;
  // without the binding check the outgoing document flashes in the new slot.
  if (
    allowEditorMount &&
    activeChunkId &&
    chunkId === activeChunkId &&
    boundChunkId === activeChunkId
  ) {
    return 'active';
  }

  // Key loading UI from the preparation target, not from showEditor.
  if (loadingChunkId && chunkId === loadingChunkId) {
    return 'loading';
  }

  // End-of-chunk cue: next slot shows skeleton instead of blank reserved space.
  if (peekChunkId && chunkId === peekChunkId) {
    return 'peek';
  }

  return 'placeholder';
};

/** Next chunk after the active one, if any. */
export const nextChunkIdAfter = (
  index: TranscriptChunkMeta[],
  activeChunkId: string | null | undefined,
): string | null => {
  if (!activeChunkId || !index.length) return null;
  const i = index.findIndex((m) => m.chunkId === activeChunkId);
  if (i < 0 || i >= index.length - 1) return null;
  return index[i + 1].chunkId;
};

/** Previous chunk before the active one, if any. */
export const prevChunkIdBefore = (
  index: TranscriptChunkMeta[],
  activeChunkId: string | null | undefined,
): string | null => {
  if (!activeChunkId || !index.length) return null;
  const i = index.findIndex((m) => m.chunkId === activeChunkId);
  if (i <= 0) return null;
  return index[i - 1].chunkId;
};

export type SeamPeekAlign = 'start' | 'end';

export type SeamPeekTarget = {
  chunkId: string;
  align: SeamPeekAlign;
  /** Upward seam only: px to fill from viewport down to the active chunk. */
  skeletonHeight?: number;
};

/**
 * Cumulative top offset of a chunk inside the shell (px).
 */
export const chunkOffsetTopPx = (
  index: TranscriptChunkMeta[],
  chunkId: string,
  heightFor: (meta: TranscriptChunkMeta) => number,
): number => {
  let top = 0;
  for (let i = 0; i < index.length; i++) {
    if (index[i].chunkId === chunkId) return top;
    top += heightFor(index[i]);
  }
  return top;
};

/**
 * Which neighbor chunk should show a seam skeleton, and where to align it.
 * Downward: skeleton at the top of the next slot when the active end is visible.
 * Upward: skeleton at the bottom of the previous slot when the viewport top has
 * entered that reserved slot (active first row is below the viewport top).
 */
export const resolveSeamPeekTarget = (
  index: TranscriptChunkMeta[],
  activeChunkId: string | null | undefined,
  scrollTop: number,
  viewportHeight: number,
  heightFor: (meta: TranscriptChunkMeta) => number,
  leadPx: number = CHUNK_SEAM_PEEK_LEAD_PX,
): SeamPeekTarget | null => {
  if (!activeChunkId || !index.length) return null;

  const activeMeta = index.find((m) => m.chunkId === activeChunkId);
  if (!activeMeta) return null;

  const activeTop = chunkOffsetTopPx(index, activeChunkId, heightFor);
  const activeBottom = activeTop + heightFor(activeMeta);
  const viewTop = scrollTop;
  const viewBottom = scrollTop + viewportHeight;

  const endVisible =
    activeBottom >= viewTop && activeBottom <= viewBottom + leadPx;
  if (endVisible) {
    const nextId = nextChunkIdAfter(index, activeChunkId);
    if (nextId) return { chunkId: nextId, align: 'start' };
  }

  const prevId = prevChunkIdBefore(index, activeChunkId);
  if (!prevId) return null;

  const upwardSeam = viewTop < activeTop;
  const startVisible = activeTop >= viewTop - leadPx && activeTop <= viewBottom;
  if (upwardSeam || startVisible) {
    const prevTop = chunkOffsetTopPx(index, prevId, heightFor);
    const prevMeta = index.find((m) => m.chunkId === prevId);
    const minPx = CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX;
    const fillFrom = Math.max(viewTop, prevTop);
    const fillPx = Math.max(minPx, activeTop - fillFrom);
    return {
      chunkId: prevId,
      align: 'end',
      skeletonHeight: Math.min(
        heightFor(prevMeta ?? activeMeta),
        fillPx + CHUNK_HEIGHT_EPSILON_PX,
      ),
    };
  }

  return null;
};

/** Skeleton alignment when a chunk slot is preparing (loading). */
export const seamPeekAlignForLoadingChunk = (
  index: TranscriptChunkMeta[],
  loadingChunkId: string | null | undefined,
  referenceChunkId: string | null | undefined,
): SeamPeekAlign => {
  if (!loadingChunkId || !referenceChunkId) return 'start';
  const loading = index.find((m) => m.chunkId === loadingChunkId);
  const reference = index.find((m) => m.chunkId === referenceChunkId);
  if (!loading || !reference) return 'start';
  return loading.ordinal < reference.ordinal ? 'end' : 'start';
};

/** Height for an upward seam skeleton (fills viewport → active chunk). */
export const resolveUpwardSeamSkeletonHeight = (
  index: TranscriptChunkMeta[],
  prevChunkId: string,
  activeChunkId: string,
  scrollTop: number,
  heightFor: (meta: TranscriptChunkMeta) => number,
): number => {
  const prevMeta = index.find((m) => m.chunkId === prevChunkId);
  if (!prevMeta) {
    return CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX;
  }
  const prevTop = chunkOffsetTopPx(index, prevChunkId, heightFor);
  const activeTop = chunkOffsetTopPx(index, activeChunkId, heightFor);
  const minPx = CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX;
  const fillFrom = Math.max(scrollTop, prevTop);
  const fillPx = Math.max(minPx, activeTop - fillFrom);
  return Math.min(heightFor(prevMeta), fillPx + CHUNK_HEIGHT_EPSILON_PX);
};

/** Row count for a skeleton block; keeps DOM bounded for tall fills. */
export const seamSkeletonRowCount = (
  skeletonHeightPx: number,
  maxRows: number = 40,
  /** Upward seam: render one extra row; parent clips overflow at the top. */
  overfill = false,
): number => {
  const base = Math.ceil(skeletonHeightPx / CHUNK_PLACEHOLDER_ROW_PX);
  const rows = overfill ? base + 1 : base;
  return Math.min(maxRows, Math.max(1, rows));
};

/** Merge consecutive chunk node arrays for the active window W. */
export const mergeWindowNodes = <T extends TranscriptBlock>(
  chunks: Record<string, T[]>,
  index: TranscriptChunkMeta[],
  window: ActiveWindow,
): T[] => {
  const out: T[] = [];
  for (let ord = window.startOrdinal; ord <= window.endOrdinal; ord++) {
    const meta = index[ord];
    if (!meta) continue;
    const part = chunks[meta.chunkId];
    if (!part?.length) continue;
    const count = Math.min(part.length, meta.blockCount);
    out.push(...part.slice(0, count));
  }
  return out;
};

/** Deep clone for Slate mount — strips internal keys from prior editor instances. */
export const cloneNodesForSlateEditor = <T extends TranscriptBlock>(
  nodes: T[],
): T[] => {
  if (!nodes.length) return [];
  if (typeof structuredClone === 'function') {
    return structuredClone(nodes);
  }
  return JSON.parse(JSON.stringify(nodes)) as T[];
};

/** Detect overlapping/corrupt chunk merges (duplicate caption starts). */
export const mergedWindowHasDuplicateStarts = (
  nodes: TranscriptBlock[],
): boolean => {
  const seen = new Set<number>();
  for (const node of nodes) {
    const start = node?.data?.start;
    if (typeof start !== 'number' || Number.isNaN(start)) continue;
    if (seen.has(start)) return true;
    seen.add(start);
  }
  return false;
};

/** True when a merged window is missing blocks or has corrupt chunk joins. */
export const mergedWindowNeedsRepair = (
  index: TranscriptChunkMeta[],
  chunks: Record<string, TranscriptBlock[]>,
  window: ActiveWindow,
  merged: TranscriptBlock[],
): boolean => {
  let expected = 0;
  for (let ord = window.startOrdinal; ord <= window.endOrdinal; ord++) {
    const meta = index[ord];
    if (!meta) return true;
    expected += meta.blockCount;
    const part = chunks[meta.chunkId];
    if (!part?.length || part.length < meta.blockCount) return true;
  }
  if (expected > 0 && merged.length !== expected) return true;
  return mergedWindowHasDuplicateStarts(merged);
};

/** Split a merged editor tree back into per-chunk slices using global row indices. */
export const splitEditorNodesByChunkBoundaries = <T extends TranscriptBlock>(
  editorChildren: T[],
  index: TranscriptChunkMeta[],
  window: ActiveWindow,
  chunks?: Record<string, T[]>,
): Map<string, T[]> => {
  const map = new Map<string, T[]>();
  let cursor = 0;
  for (let ord = window.startOrdinal; ord <= window.endOrdinal; ord++) {
    const meta = index[ord];
    if (!meta) continue;
    const count = chunks?.[meta.chunkId]?.length ?? meta.blockCount;
    const slice = editorChildren.slice(cursor, cursor + count);
    if (slice.length) map.set(meta.chunkId, slice);
    cursor += count;
  }
  return map;
};

/** Build cumulative chunk bands for scroll/window geometry. */
export const buildChunkBands = (
  index: TranscriptChunkMeta[],
  heightFor: (meta: TranscriptChunkMeta) => number,
): ChunkBand[] => {
  let offsetTop = 0;
  return index.map((meta) => {
    const height = heightFor(meta);
    const band: ChunkBand = {
      ordinal: meta.ordinal,
      chunkId: meta.chunkId,
      offsetTop,
      height,
    };
    offsetTop += height;
    return band;
  });
};

export const windowsEqual = (a: ActiveWindow, b: ActiveWindow): boolean =>
  a.startOrdinal === b.startOrdinal && a.endOrdinal === b.endOrdinal;

/** ±1 ordinal slide with overlapping chunks (DWCM window shift). */
export const isIncrementalWindowSlide = (
  prev: ActiveWindow,
  next: ActiveWindow,
): boolean =>
  windowsOverlap(prev, next) &&
  next.startOrdinal >= prev.startOrdinal - 1 &&
  next.startOrdinal <= prev.startOrdinal + 1 &&
  next.endOrdinal >= prev.endOrdinal - 1 &&
  next.endOrdinal <= prev.endOrdinal + 1;

/** Single-chunk upward slide, e.g. [2,3,4] → [1,2,3]. */
export const isSingleChunkUpwardSlide = (
  prev: ActiveWindow,
  next: ActiveWindow,
): boolean =>
  isIncrementalWindowSlide(prev, next) &&
  next.startOrdinal === prev.startOrdinal - 1 &&
  next.endOrdinal >= prev.startOrdinal &&
  next.endOrdinal <= prev.endOrdinal;

const windowsOverlap = (a: ActiveWindow, b: ActiveWindow): boolean =>
  a.startOrdinal <= b.endOrdinal && b.startOrdinal <= a.endOrdinal;

/** Downward: append when viewport bottom enters bottom α zone of C_end. */
export const shouldAppendNextChunk = (
  input: ScrollWindowInput,
  window: ActiveWindow,
  indexLength: number,
): boolean => {
  if (window.endOrdinal >= indexLength - 1) return false;
  if (window.endOrdinal - window.startOrdinal + 1 >= CHUNK_WINDOW_MAX) {
    return false;
  }
  const alpha = input.alpha ?? CHUNK_WINDOW_ALPHA;
  const endBand = bandForOrdinal(input.bands, window.endOrdinal);
  if (!endBand || endBand.height <= 0) return false;
  const triggerTop = endBand.offsetTop + (1 - alpha) * endBand.height;
  return input.scrollTop + input.viewportHeight >= triggerTop;
};

/** Upward: prepend when viewport top enters top α zone of C_start. */
export const shouldPrependPrevChunk = (
  input: ScrollWindowInput,
  window: ActiveWindow,
): boolean => {
  if (window.startOrdinal <= 0) return false;
  if (window.endOrdinal - window.startOrdinal + 1 >= CHUNK_WINDOW_MAX) {
    return false;
  }
  const alpha = input.alpha ?? CHUNK_WINDOW_ALPHA;
  const startBand = bandForOrdinal(input.bands, window.startOrdinal);
  if (!startBand || startBand.height <= 0) return false;
  const triggerBottom = startBand.offsetTop + alpha * startBand.height;
  return input.scrollTop <= triggerBottom;
};

export type WindowBoundaryTriggers = {
  top: boolean;
  bottom: boolean;
};

/**
 * True when the viewport scroll position sits in the top/bottom α zones of the
 * active window — independent of whether another chunk can still be merged in.
 */
export const viewportInWindowBoundaryZone = (
  input: ScrollWindowInput,
  window: ActiveWindow,
): WindowBoundaryTriggers => {
  const alpha = input.alpha ?? CHUNK_WINDOW_ALPHA;
  const startBand = bandForOrdinal(input.bands, window.startOrdinal);
  const endBand = bandForOrdinal(input.bands, window.endOrdinal);
  let top = false;
  let bottom = false;

  if (startBand && startBand.height > 0 && window.startOrdinal > 0) {
    const triggerBottom = startBand.offsetTop + alpha * startBand.height;
    top = input.scrollTop <= triggerBottom;
  }
  if (endBand && endBand.height > 0) {
    const triggerTop = endBand.offsetTop + (1 - alpha) * endBand.height;
    bottom = input.scrollTop + input.viewportHeight >= triggerTop;
  }

  return { top, bottom };
};

/** True when the viewport has reached the top/bottom α trigger zones of the window. */
export const windowBoundaryTriggers = (
  index: TranscriptChunkMeta[],
  window: ActiveWindow,
  scrollTop: number,
  viewportHeight: number,
  heightFor: (meta: TranscriptChunkMeta) => number,
  alpha: number = CHUNK_WINDOW_ALPHA,
): WindowBoundaryTriggers => {
  const bands = buildChunkBands(index, heightFor);
  return viewportInWindowBoundaryZone(
    { scrollTop, viewportHeight, bands, alpha },
    window,
  );
};

export type BoundarySkeletonOverlayPx = {
  topPx: number;
  bottomPx: number;
  /** Editor-relative top offset where the bottom overlay band begins (at the α trigger line). */
  bottomOffsetTopPx: number;
};

/**
 * Skeleton overlay from each α trigger line to the window content edge.
 * Heights are relative to the merged editor window (editorWindowRef origin).
 * Pass measuredEditorHeightPx from the live Slate editor for accurate placement.
 */
export const resolveBoundarySkeletonOverlayPx = (
  index: TranscriptChunkMeta[],
  window: ActiveWindow,
  scrollTop: number,
  viewportHeight: number,
  heightFor: (meta: TranscriptChunkMeta) => number,
  alpha: number = CHUNK_WINDOW_ALPHA,
  measuredEditorHeightPx?: number,
): BoundarySkeletonOverlayPx => {
  const bands = buildChunkBands(index, heightFor);
  const zones = viewportInWindowBoundaryZone(
    { scrollTop, viewportHeight, bands, alpha },
    window,
  );
  const windowBand = windowContentBand(index, window, heightFor);
  const windowEstH = Math.max(0, windowBand.bottom - windowBand.top);
  const scale =
    measuredEditorHeightPx && windowEstH > 0
      ? measuredEditorHeightPx / windowEstH
      : 1;
  const windowH = measuredEditorHeightPx ?? windowEstH;
  const minPx = CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX;

  let topPx = 0;
  let bottomPx = 0;
  let bottomOffsetTopPx = 0;

  if (zones.top && window.startOrdinal > 0) {
    const startBand = bandForOrdinal(bands, window.startOrdinal);
    if (startBand) {
      const triggerBottomDoc =
        startBand.offsetTop + alpha * startBand.height;
      topPx = Math.max(
        minPx,
        Math.round((triggerBottomDoc - windowBand.top) * scale),
      );
    }
  }

  if (zones.bottom) {
    const endBand = bandForOrdinal(bands, window.endOrdinal);
    if (endBand) {
      const triggerTopDoc =
        endBand.offsetTop + (1 - alpha) * endBand.height;
      bottomOffsetTopPx = Math.max(
        0,
        Math.round((triggerTopDoc - windowBand.top) * scale),
      );
      bottomPx = Math.max(minPx, Math.round(windowH - bottomOffsetTopPx));
    }
  }

  return { topPx, bottomPx, bottomOffsetTopPx };
};

/**
 * Evict the far chunk once the viewport is securely inside the newly entered
 * neighbor chunk (> cleanup ratio).
 */
export const shouldEvictFarChunk = (
  input: ScrollWindowInput,
  window: ActiveWindow,
  direction: 'down' | 'up',
): boolean => {
  if (window.endOrdinal <= window.startOrdinal) return false;
  const ratio = CHUNK_WINDOW_CLEANUP_RATIO;
  if (direction === 'down') {
    const targetOrd = window.endOrdinal;
    const band = bandForOrdinal(input.bands, targetOrd);
    if (!band || band.height <= 0) return false;
    const penetration =
      (input.scrollTop + input.viewportHeight * 0.35 - band.offsetTop) /
      band.height;
    return penetration >= ratio && window.startOrdinal < targetOrd;
  }
  const targetOrd = window.startOrdinal;
  const band = bandForOrdinal(input.bands, targetOrd);
  if (!band || band.height <= 0) return false;
  const viewMid = input.scrollTop + input.viewportHeight * 0.35;
  const penetration = (viewMid - band.offsetTop) / band.height;
  return penetration >= ratio && window.endOrdinal > targetOrd;
};

/** Ordinal whose cumulative band contains `scrollOffset` (px from doc top). */
export const findOrdinalAtScrollOffset = (
  index: TranscriptChunkMeta[],
  scrollOffset: number,
  heightFor: (meta: TranscriptChunkMeta) => number,
): number => {
  if (!index.length) return 0;
  let acc = 0;
  for (let i = 0; i < index.length; i++) {
    const h = heightFor(index[i]);
    if (scrollOffset < acc + h || i === index.length - 1) return i;
    acc += h;
  }
  return index.length - 1;
};

/**
 * Ideal active window for the viewport: primary chunk K, plus K±1 when the
 * scroll position sits on a chunk seam (bottom/top α zones).
 */
export const resolveWindowForViewport = (
  index: TranscriptChunkMeta[],
  scrollTop: number,
  viewportHeight: number,
  heightFor: (meta: TranscriptChunkMeta) => number,
  alpha: number = CHUNK_WINDOW_ALPHA,
): ActiveWindow => {
  if (!index.length) return { startOrdinal: 0, endOrdinal: 0 };

  const viewMid = scrollTop + viewportHeight * 0.35;
  const viewBottom = scrollTop + viewportHeight;
  const centerOrd = findOrdinalAtScrollOffset(index, viewMid, heightFor);

  let start = centerOrd;
  let end = centerOrd;

  const bands = buildChunkBands(index, heightFor);
  const input: ScrollWindowInput = { scrollTop, viewportHeight, bands, alpha };
  const centerOnly: ActiveWindow = {
    startOrdinal: centerOrd,
    endOrdinal: centerOrd,
  };

  if (
    centerOrd < index.length - 1 &&
    shouldAppendNextChunk(input, centerOnly, index.length)
  ) {
    end = centerOrd + 1;
  }

  if (centerOrd > 0 && shouldPrependPrevChunk(input, centerOnly)) {
    start = centerOrd - 1;
  }

  const centerBand = bands[centerOrd];
  if (centerBand) {
    const chunkBottom = centerBand.offsetTop + centerBand.height;
    const chunkTop = centerBand.offsetTop;
    if (
      viewBottom > chunkBottom &&
      end < centerOrd + 1 &&
      centerOrd < index.length - 1
    ) {
      end = centerOrd + 1;
    }
    if (scrollTop < chunkTop && start > centerOrd - 1 && centerOrd > 0) {
      start = centerOrd - 1;
    }
  }

  start = Math.max(0, start);
  end = Math.min(index.length - 1, end);

  const clampWindowToMax = (s: number, e: number): ActiveWindow => {
    let ss = s;
    let ee = e;
    if (ee - ss + 1 <= CHUNK_WINDOW_MAX) {
      return { startOrdinal: ss, endOrdinal: ee };
    }
    const dropStart = centerOrd - ss;
    const dropEnd = ee - centerOrd;
    if (dropStart >= dropEnd) {
      ss = ee - (CHUNK_WINDOW_MAX - 1);
    } else {
      ee = ss + (CHUNK_WINDOW_MAX - 1);
    }
    ss = Math.max(0, ss);
    ee = Math.min(index.length - 1, ee);
    if (ee - ss + 1 > CHUNK_WINDOW_MAX) {
      ee = ss + CHUNK_WINDOW_MAX - 1;
    }
    return { startOrdinal: ss, endOrdinal: ee };
  };

  // Viewport may sit in a far-off spacer while center ordinal is still inside W.
  // Pull adjacent chunks in until the merged band covers the viewport (or |W|=MAX).
  for (let guard = 0; guard < index.length; guard++) {
    const band = windowContentBand(
      index,
      { startOrdinal: start, endOrdinal: end },
      heightFor,
    );
    let changed = false;
    if (viewBottom > band.bottom && end < index.length - 1) {
      end += 1;
      changed = true;
    }
    if (scrollTop < band.top && start > 0) {
      start -= 1;
      changed = true;
    }
    if (!changed) break;
    ({ startOrdinal: start, endOrdinal: end } = clampWindowToMax(start, end));
  }

  return clampContiguousWindow(
    { startOrdinal: start, endOrdinal: end },
    index.length,
    CHUNK_WINDOW_MAX,
    centerOrd,
  );
};

/** True when the viewport seam needs an adjacent chunk not yet in the window. */
export const viewportNeedsBoundaryExpansion = (
  index: TranscriptChunkMeta[],
  window: ActiveWindow,
  scrollTop: number,
  viewportHeight: number,
  heightFor: (meta: TranscriptChunkMeta) => number,
): boolean => {
  const target = resolveWindowForViewport(
    index,
    scrollTop,
    viewportHeight,
    heightFor,
  );
  return !windowsEqual(window, target);
};

const bandForOrdinal = (
  bands: ChunkBand[],
  ordinal: number,
): ChunkBand | undefined => bands.find((b) => b.ordinal === ordinal);
export const viewportCenterOrdinal = (
  scrollTop: number,
  viewportHeight: number,
  index: TranscriptChunkMeta[],
  heightFor: (meta: TranscriptChunkMeta) => number,
): number => {
  const viewMid = scrollTop + viewportHeight * 0.35;
  return findOrdinalAtScrollOffset(index, viewMid, heightFor);
};

/** True when viewport center ordinal falls outside [startOrdinal, endOrdinal]. */
export const viewportOrdinalOutsideWindow = (
  index: TranscriptChunkMeta[],
  window: ActiveWindow,
  scrollTop: number,
  viewportHeight: number,
  heightFor: (meta: TranscriptChunkMeta) => number,
): boolean => {
  const centerOrd = viewportCenterOrdinal(
    scrollTop,
    viewportHeight,
    index,
    heightFor,
  );
  return centerOrd < window.startOrdinal || centerOrd > window.endOrdinal;
};

/** Pixel band occupied by merged window chunks (excludes far-off spacers). */
export const windowContentBand = (
  index: TranscriptChunkMeta[],
  window: ActiveWindow,
  heightFor: (meta: TranscriptChunkMeta) => number,
): { top: number; bottom: number } => {
  let top = 0;
  for (let i = 0; i < window.startOrdinal && i < index.length; i++) {
    top += heightFor(index[i]);
  }
  let bottom = top;
  for (
    let i = window.startOrdinal;
    i <= window.endOrdinal && i < index.length;
    i++
  ) {
    bottom += heightFor(index[i]);
  }
  return { top, bottom };
};

/**
 * True when the viewport sits in a top/bottom spacer (blank area) instead of
 * the merged editor band. Uses ordinal math — pixel bands drift vs live Slate.
 */
export const viewportIsInBlankSpacer = (
  index: TranscriptChunkMeta[],
  window: ActiveWindow,
  scrollTop: number,
  viewportHeight: number,
  heightFor: (meta: TranscriptChunkMeta) => number,
): boolean =>
  viewportOrdinalOutsideWindow(
    index,
    window,
    scrollTop,
    viewportHeight,
    heightFor,
  );

export type ViewportSpacerIntersection = {
  top: boolean;
  bottom: boolean;
};

/** True when the viewport edge crosses into the reserved top/bottom spacer. */
export const viewportSpacerIntersection = (
  index: TranscriptChunkMeta[],
  window: ActiveWindow,
  scrollTop: number,
  viewportHeight: number,
  heightFor: (meta: TranscriptChunkMeta) => number,
): ViewportSpacerIntersection =>
  viewportSpacerIntersectionForBand(
    scrollTop,
    viewportHeight,
    windowContentBand(index, window, heightFor),
  );

/** Spacer intersection against a precomputed content band (live DOM or estimate). */
export const viewportSpacerIntersectionForBand = (
  scrollTop: number,
  viewportHeight: number,
  band: { top: number; bottom: number },
): ViewportSpacerIntersection => {
  const viewBottom = scrollTop + viewportHeight;
  return {
    top: scrollTop < band.top,
    bottom: viewBottom > band.bottom,
  };
};

/** True when the active window should shift or expand for the viewport. */
export const viewportNeedsWindowCatchUp = (
  index: TranscriptChunkMeta[],
  window: ActiveWindow,
  scrollTop: number,
  viewportHeight: number,
  heightFor: (meta: TranscriptChunkMeta) => number,
): boolean => {
  const spacers = viewportSpacerIntersection(
    index,
    window,
    scrollTop,
    viewportHeight,
    heightFor,
  );
  if (spacers.top || spacers.bottom) return true;
  return (
    viewportOrdinalOutsideWindow(
      index,
      window,
      scrollTop,
      viewportHeight,
      heightFor,
    ) ||
    viewportNeedsBoundaryExpansion(
      index,
      window,
      scrollTop,
      viewportHeight,
      heightFor,
    )
  );
};

/** Visible px of a spacer band inside the viewport (for seam skeleton fill). */
export const visibleSpacerFillPx = (
  position: 'before' | 'after',
  spacerHeight: number,
  scrollTop: number,
  viewportHeight: number,
  bandTop: number,
  bandBottom: number,
): number => {
  const minPx = CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX;
  const viewBottom = scrollTop + viewportHeight;
  const visible =
    position === 'before'
      ? Math.max(0, bandTop - scrollTop)
      : Math.max(0, viewBottom - bandBottom);
  return Math.min(spacerHeight, Math.max(minPx, visible));
};
